//  ----------------------------------------------------------------------------
//  Dependencies
//  ----------------------------------------------------------------------------
//  -- Containers ---------------------
import React, { Suspense } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
// Keep hooks and utilities as normal imports
import { useTimmerRunning } from 'store/timmer/selectors';
import { useCurrentUser } from 'store/users/selectors';
import { generateRoutes } from 'utils/config/navigation';
import Wrapper from 'features/Wrapper';
import CustomPrompt from 'components/UI/Prompt';


import AssignmentList from 'components/AssignmentLists';
import LessonsContainer from 'containers/1WayLession';
import AssessmentContainer from 'containers/Assessment';
import AssessmentAp from 'containers/AssessmentAp';
import BalconCultural from 'containers/BalconCultural';
import Comunidad from 'containers/Comunidad';
import ComunidadBook from 'containers/ComunidadBook';
import Conectandome from 'containers/Conectandome';
import ConectandomeBook from 'containers/ConectandomeBook';
import EbookContainer from 'containers/Ebook';
import GraphicOrganizer from 'containers/GrapicOrganizerNew';
import InformateContainer from 'containers/Informate';
import LoginContainer from 'containers/Login';
import NotFoundContainer from 'containers/NotFound';
// Lazy loaded components for selected routes
const Privacy = React.lazy(() => import('containers/Privacy/index'));
const PrivacyPolicy = React.lazy(() => import('containers/PrivacyPolicy'));
const StudentPolicy = React.lazy(() => import('containers/StudentPolicy'));
const TermsConditions = React.lazy(() => import('containers/termCondition'));
const PrintAssignmentHome = React.lazy(() => import('./PrintAssignmentHome'));

import ResourcesContainer from 'containers/Resources';
import VideosContainer from 'containers/Videos';
import AP from 'containers/ap';
import ApBook from 'containers/apBook';
import TeacherStrategies from 'modules/1wayTeacherVideo/TeacherStrategies';
import AddDistricts from 'containers/AddDistricts';
import AddSchools from 'containers/AddSchools';
import AddStandard from 'containers/AddStandard';
import AddTeachers from 'containers/AddTeachers';
import ApGradeBook from 'containers/ApGradeBook';
import BalconCulturalBook from 'containers/BalconCulturalBook';
import CanvasUserView from 'containers/CanvasUserView';
import Classlink from 'containers/Classlink';
import Clever from 'containers/Clever';
import GoogleUserView from 'containers/GoogleUserView';
import GridStoryLevel from 'containers/GridStoryLevel';
import GameEbook from 'containers/OneGame/GameEbook';
import HomeGame from 'containers/OneGame/HomeGame';
import Schoology from 'containers/Schoology';
import Settings from 'containers/Settings';
import StoryBook from 'containers/StoryBook';
import StoryLavel from 'containers/StoryLevels';
import WhatsNew from 'containers/WhatsNew';
import EditBooks from 'containers/addBook/editBookList';
import Chat from 'containers/chat';
import Notification from 'containers/notification';
import TeacherWhatsNew from 'modules/WhatsNew/teacherWhatsNew';
import HomeScreen from './Home';
import HowToVideos from './HowtoVideos';
import InformateEbook from './InformateEbook';
import InformateEbookBook from './InformateEbookBook';
import Logs from './Logs';
import ClassPasswords from './VideoLinkDetails/ClassPasswords';
import DataDriven from './VideoLinkDetails/DataDriven';
import Differentiation from './VideoLinkDetails/Differentiation';
import HeritageLanguage from './VideoLinkDetails/HeritageLanguage';
import TeachingHeritage from './VideoLinkDetails/TeachingHeritage';
import PlacementExam from './placementExam';
import PlacementExamFinishSubmission from './placementExam/PlacementExamFinishSubmission';
import PlacementExamQuestion from './placementExam/PlacementExamQuestion';
import PlacementExamStudentForm from './placementExam/PlacementExamStudentForm';
import PlacementExamSubmission from './placementExam/PlacementExamSubmission';
import ParentsStudentHome from 'modules/parentsStudentHome';
import LoadingComponent from 'components/UI/Loading/LoadingComponent';

//  ----------------------------------------------------------------------------
//  Routes
//  ----------------------------------------------------------------------------
const AllRoutes = (props) => {
  const currentUser = useCurrentUser();
  const [timmerRunning] = useTimmerRunning();
  const location = useLocation();

  return (
      <Routes>
        <Route element={<HomeGame {...props} />} path="/home-game" />
        <Route element={<GameEbook {...props} />} path="/game-book" />
        <Route element={<HomeScreen {...props} />} path="/" />
        <Route element={<LoginContainer {...props} />} path="/login" />
        <Route 
          element={
            <Suspense fallback={<LoadingComponent />}>
              <Privacy {...props} />
            </Suspense>
          } 
          path="/privacy" 
        />
        <Route 
          element={
            <Suspense fallback={<LoadingComponent />}>
              <PrivacyPolicy {...props} />
            </Suspense>
          } 
          path="/privacy-policy" 
        />
        <Route 
          element={
            <Suspense fallback={<LoadingComponent />}>
              <TermsConditions {...props} />
            </Suspense>
          } 
          path="/terms-conditions" 
        />
        <Route 
          element={
            <Suspense fallback={<LoadingComponent />}>
              <StudentPolicy {...props} />
            </Suspense>
          } 
          path="/student-information-privacy-notice" 
        />
        <Route 
          element={
            <Suspense fallback={<LoadingComponent />}>
              <PrintAssignmentHome {...props} />
            </Suspense>
          } 
          path="/print_assignment" 
        />
        <Route element={<PlacementExam {...props} />} path="/placement-exam" />
        <Route element={<PlacementExamStudentForm {...props} />} path="/placement-exam/form" />
        <Route element={<PlacementExamQuestion {...props} />} path="/placement-exam/exam" />
        <Route element={<PlacementExamSubmission {...props} />} path="/placement-exam/submissions" />
        <Route element={<PlacementExamFinishSubmission {...props} />} path="/placement-exam/final-submission" />

        {/* Protected Routes */}
        {currentUser?.id && currentUser?.role && currentUser?.active ? (
          <Route
            path="*"
            element={
              <Wrapper>
                <CustomPrompt
                  when={timmerRunning}
                  message={(loc) =>
                    loc.pathname === location.pathname &&
                    loc.search.includes("activity")
                      ? true
                      : `Timer-based assignments can't be "finished later",\nit will be submitted automatically.`
                  }
                />
                  <Routes>
                    <Route path="/ebook" element={<EbookContainer {...props} />} />
                    <Route path="/graphic_organizers" element={<GraphicOrganizer {...props} />} />
                    <Route element={<TeacherStrategies {...props} />} path="/Strategies" />
                    <Route element={<VideosContainer {...props} />} path="/video" />
                    <Route element={<InformateContainer {...props} />} path="/1nformate" />
                    <Route element={<ResourcesContainer {...props} />} path="/resources" />
                    <Route element={<AssessmentContainer {...props} />} path="/exams" />
                    <Route element={<LessonsContainer {...props} />} path="/OneWayLessons" />
                    <Route element={<AssessmentAp {...props} />} path="/ap-exams" />
                    <Route element={<BalconCultural {...props} />} path="/balconCultural" />
                    <Route element={<Conectandome {...props} />} path="/Conectandome" />
                    <Route element={<ConectandomeBook {...props} />} path="/ConectandomeBook" />
                    <Route element={<BalconCulturalBook {...props} />} path="/balconCulturalBook" />
                    <Route element={<StoryBook {...props} />} path="/stories" />
                    <Route element={<Comunidad {...props} />} path="/comunidad" />
                    <Route element={<ComunidadBook {...props} />} path="/comunidadBook" />
                    <Route element={<AP {...props} />} path="/ap" />
                    <Route element={<ApBook {...props} />} path="/apBook" />
                    <Route element={<InformateEbook {...props} />} path="/informate" />
                    <Route element={<InformateEbookBook {...props} />} path="/informateBook" />
                    <Route element={<HowToVideos {...props} />} path="/how-to-videos" />
                    <Route element={<Logs {...props} />} path="/logs" />
                    <Route element={<Schoology {...props} />} path="/schoology" />
                    <Route element={<Classlink {...props} />} path="/classlink" />
                    <Route element={<CanvasUserView {...props} />} path="/canvas" />
                    <Route element={<GoogleUserView {...props} />} path="/google" />
                    <Route element={<Clever {...props} />} path="/clever" />
                    <Route element={<EditBooks {...props} />} path="/editBooks" />
                    <Route element={<AddDistricts {...props} />} path="/addDistrict" />
                    <Route element={<AddStandard {...props} />} path="/addStandard" />
                    <Route element={<AddSchools {...props} />} path="/add-schools" />
                    <Route element={<AddTeachers {...props} />} path="/add-users" />
                    <Route element={<Settings {...props} />} path="/settings" />
                    <Route element={<TeachingHeritage {...props} />} path="/teaching-heritage" />
                    <Route element={<Differentiation {...props} />} path="/differentiation" />
                    <Route element={<ClassPasswords {...props} />} path="/class-passwords" />
                    <Route element={<DataDriven {...props} />} path="/data-driven" />
                    <Route element={<HeritageLanguage {...props} />} path="/heritage-language" />
                    <Route element={<StoryLavel {...props} />} path="/story-levels" />
                    <Route element={<ApGradeBook {...props} />} path="/ap/gradbook" />
                    <Route element={<GridStoryLevel {...props} />} path="/story/gradebook" />
                    <Route element={<Chat {...props} />} path="/chat" />
                    <Route element={<Notification {...props} />} path="/notification" />
                    <Route element={<AssignmentList {...props} />} path="/assignmentList" />
                    <Route element={<WhatsNew {...props} />} path="/whatsNew" />
                    <Route element={<TeacherWhatsNew {...props} />} path="/whats-new" />
                    <Route element={<ParentsStudentHome {...props} />} path="/parents-student-home" />
                    {generateRoutes(props.store.getState().currentUser).map((r) => (
                      <Route key={r.path} path={r.path} element={<r.component {...props} />} />
                    ))}
                    <Route path="*" element={<NotFoundContainer />} />
                  </Routes>
              </Wrapper>
            }
          />
        ) : (
          <Route path="*" element={<Navigate to="/" />} />
        )}
        <Route element={<NotFoundContainer {...props} />} path="*" />
      </Routes>
  );
};

export default AllRoutes; 